import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import {
  ConfirmDialogService,
  countries,
  Destroyable,
  genders,
  isRestrictingUsers,
  isSomething,
  languages,
  notAllNumbersValidator,
  ParsedGooglePlace,
  RemoveUser,
  Rental,
  restrictUser,
  restrictUserAll,
  Role,
  SaveForm,
  selectAllRentals,
  selectAllUsers,
  selectRoles,
  Toaster,
  unRestrictUser,
  unRestrictUserAll,
  untilDestroy,
  UpdateUser,
  UpdateUserSettings,
  User,
  USER_ROLES,
  UserService,
  UserSettingsForm,
  UpdateUserMFA,
  selectCurrentAccount,
} from '@tokeet/tv3-platform'
import * as fromRoot from '@tv3/store/state'
import * as R from 'ramda'
import { BehaviorSubject, Observable } from 'rxjs'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { alphanumericNameValidator, isEmptyTable, localeCompareSort, ActionFailed } from '@tokeet-frontend/utility'
import { userRoleToString } from '@tv3/utils/functions/user-role-to-string'
import { AuthService } from '@tv3/services/auth.service'
import { userRoleToNumber } from '@tv3/utils/functions/user-role-to-number'
import { IsRestrictedPipe } from '@tv3/containers/settings/user-settings/overlays/is-restricted.pipe'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatSort } from '@angular/material/sort'
import { MatCheckboxChange } from '@angular/material/checkbox'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MapsAPILoader } from '@agm/core'
import { map, switchMap } from 'rxjs/operators'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { mfaDeliveryMethodOptions, MFADeliveryMethod } from '@tokeet/tv3-platform'
import { UserMFADialogService } from '../user-mfa-dialog/user-mfa-dialog.service'

@Component({
  selector: 'app-user-overlay',
  templateUrl: './user-overlay.component.html',
  styleUrls: ['./user-overlay.component.scss'],
})
export class UserOverlayComponent extends Destroyable implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) rentalTablePaginator: MatPaginator
  @ViewChild(MatSort, { static: true }) rentalTableSort: MatSort

  countryList = countries
  languages = languages
  genders = genders
  roles$ = this.store.pipe(select(selectRoles)) as Observable<Role[]>
  aiResponderSub$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)),
    map((res) => res || {})
  )

  isAccountMFAEnabled$ = this.store.pipe(
    select(selectCurrentAccount),
    map((a) => !!a.enable_2fa)
  )

  infoForm = this.fb.group({
    firstName: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        notAllNumbersValidator,
        alphanumericNameValidator,
      ],
    ],
    lastName: ['', [Validators.required, alphanumericNameValidator]],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    role: ['', [Validators.required]],
    attributes: this.fb.group({
      gender: [],
      language: [],
    }),
  })

  locationForm = this.fb.group({
    address: ['', [Validators.minLength(2), Validators.maxLength(140)]],
    city: ['', [Validators.minLength(2), Validators.maxLength(50)]],
    state: ['', [Validators.minLength(2), Validators.maxLength(50)]],
    country: [''],
    postalCode: [''],
  })

  mfaDeliveryMethodOptions = mfaDeliveryMethodOptions
  mfaForm = this.fb.group({
    status: [false],
    delivery: [MFADeliveryMethod.Email],
  })
  get isTotpSelected() {
    return this.mfaForm.get('delivery').value === MFADeliveryMethod.TOTP
  }
  refreshTotpStatus$ = new BehaviorSubject<boolean>(true)
  isTotpDeviceRegistered$ = this.refreshTotpStatus$.pipe(
    switchMap(() => this.auth.getTotpSetupStatus()),
    map((s) => s.status === 'verified')
  )

  preferenceForm = this.fb.group({
    disabled: [false],
    signature: [false],
    automata: [false],
    ownercenter: [false],
    rategenie: [false],
    webready: [false],
    checklist: [false],
    total: [false],
    ai_settings: [true],
  })

  form = this.fb.group({
    info: this.infoForm,
    location: this.locationForm,
    preference: this.preferenceForm,
  })

  user: User

  currentTab$ = new BehaviorSubject<string>('settings') // rentals

  rentalDataSource = new MatTableDataSource<Rental>([])

  displayedColumns = ['select', 'name', 'description', 'phone', 'cityView', 'countryView']

  rentals: Rental[]

  tableType = TableType.RentalsList
  isEmptyTable$ = isEmptyTable(this.rentalDataSource)
  isRestrictingUsers$ = this.store.pipe(select(isRestrictingUsers))

  users: User[]
  admins: User[]
  emails: string[]

  hideBasicInfo = false
  hideAddress = false

  ownerRentalsSearchTerm = ''
  isGoogleLoaded = false

  constructor(
    public dialogRef: MatDialogRef<UserOverlayComponent>,
    private store: Store<fromRoot.State>,
    private fb: FormBuilder,
    private toast: Toaster,
    private auth: AuthService,
    private mapsApiLoader: MapsAPILoader,
    private userService: UserService,
    private confirmDialogService: ConfirmDialogService,
    private userMFADialog: UserMFADialogService,
    @Inject(MAT_DIALOG_DATA) public data: { user: User }
  ) {
    super()
    this.mfaForm.disable()
  }

  ngOnInit() {
    this.mapsApiLoader.load().then(() => {
      this.isGoogleLoaded = true
    })
    this.rentalDataSource.paginator = this.rentalTablePaginator
    this.rentalDataSource.sort = this.rentalTableSort
    this.rentalDataSource.sortData = localeCompareSort

    this.user = this.data.user

    this.store.pipe(select(selectAllUsers), untilDestroy(this)).subscribe((users) => {
      this.users = users
      this.admins = R.filter((u: User) => u.roles && u.roles.indexOf(USER_ROLES.admin.value) > -1, users)
      this.emails = <string[]>R.pipe(
        R.filter((u: User) => u.id !== this.user.id),
        <any>R.map((u: User) => u.primaryEmail)
      )(<any>users)
      this.user = R.find((u) => u.id === this.data.user.id, users)
      this.mfaForm.patchValue({ ...(this.user.mfa_config || {}), status: !!this.user?.mfa_config?.status })
    })

    this.store.pipe(select(selectAllRentals), untilDestroy(this)).subscribe((rentals) => {
      this.rentals = rentals
      this.rentalTablePaginator.firstPage()
      this.rentalDataSource.data = R.sortBy((r) => !new IsRestrictedPipe().transform(r, this.user), rentals)
    })

    this.infoForm.patchValue({
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      email: this.user.primaryEmail,
      phone: this.user.phone,
      role: R.toString(userRoleToNumber(this.user.roles)),
      attributes: {
        gender: R.pathOr(undefined, ['attributes', 'gender'], this.user),
        language: R.pathOr(undefined, ['attributes', 'language'], this.user),
      },
    })

    this.locationForm.patchValue({
      address: R.pathOr('', ['address', 'address'], this.user),
      city: R.pathOr('', ['address', 'city'], this.user),
      state: R.pathOr('', ['address', 'state'], this.user),
      country: this.getCountryCode(),
      postalCode: R.pathOr('', ['address', 'postalCode'], this.user),
    })

    this.preferenceForm.patchValue({
      disabled: R.pathOr(false, ['settings', 'disabled'], this.user),
      signature: R.pathOr(false, ['settings', 'signature'], this.user),
      automata: R.pathOr(false, ['settings', 'automata'], this.user),
      ownercenter: R.pathOr(false, ['settings', 'ownercenter'], this.user),
      rategenie: R.pathOr(false, ['settings', 'rategenie'], this.user),
      webready: R.pathOr(false, ['settings', 'webready'], this.user),
      checklist: R.pathOr(false, ['settings', 'checklist'], this.user),
      total: R.pathOr(false, ['settings', 'total'], this.user),
      ai_settings: R.pathOr(true, ['settings', 'ai_settings'], this.user),
    })

    if (!this.canRestrictRentals()) {
      this.currentTab$.next('settings')
    }

    this.preferenceForm.valueChanges.pipe(untilDestroy(this)).subscribe((values) => {
      this.store.dispatch(UpdateUserSettings({ user: this.user, settings: values }))
    })
  }

  get isCurrentUser() {
    return this.auth.user.id === this.user.id
  }

  get isCurrentAdmin() {
    return this.auth.isAdmin()
  }

  close() {
    this.dialogRef.close()
  }

  onResetPassword() {
    this.userService.forgot(R.toLower(this.user.primaryEmail)).subscribe(
      () => this.toast.success('A reset email has been sent to the user.'),
      () => this.toast.error('Something wrong, please check users email address, and try again')
    )
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = R.find((c: any) => c.id === parsedPlace.countryShort, this.countryList)
    this.locationForm.patchValue({
      address: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city: parsedPlace.city,
      state: parsedPlace.state,
      country: R.pathOr('', ['id'], country),
    })
  }

  onResetTotp() {
    this.confirmDialogService
      .confirm({
        title: 'Warning!',
        body: 'Are you sure you want to deregister auth device?',
      })
      .pipe(switchMap(() => this.auth.deleteTotpSetup()))
      .subscribe(
        () => {
          if (this.user?.mfa_config?.delivery === MFADeliveryMethod.TOTP) {
            this.mfaForm.patchValue({ delivery: MFADeliveryMethod.Email })
            this.store.dispatch(
              UpdateUserMFA({
                userId: this.user.id,
                payload: { ...this.user?.mfa_config, delivery: MFADeliveryMethod.Email },
              })
            )
          }
          this.refreshTotpStatus$.next(true)
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
  }

  onConfigMFA() {
    this.userMFADialog.open()
  }

  onDelete() {
    this.confirmDialogService
      .confirm({
        title: 'Delete this user?',
        body: 'Are you sure you want to delete this user?',
      })
      .subscribe(() => {
        this.store.dispatch(RemoveUser({ id: this.user.id }))
        this.close()
      })
  }

  isReadOnly(user: User) {
    return userRoleToString(user.roles) === USER_ROLES.readOnly.name
  }

  isAdmin(user: User) {
    return userRoleToString(user.roles) === USER_ROLES.admin.name
  }

  isOwner(user: User) {
    return userRoleToString(user.roles) === USER_ROLES.owner.name
  }

  isStaff(user: User) {
    return userRoleToString(user.roles) === USER_ROLES.staff.name
  }

  isPropertyManager(roles: number[]) {
    return userRoleToString(roles) === USER_ROLES.manager.name
  }

  isPropertyOwner(user: User) {
    return userRoleToString(user.roles) === USER_ROLES.owner.name
  }

  isAccounting(user: User) {
    return userRoleToString(user.roles) === USER_ROLES.accounting.name
  }

  canEnableAutomata() {
    return (
      !this.isReadOnly(this.user) &&
      !this.isAdmin(this.user) &&
      !this.isOwner(this.user) &&
      !this.isStaff(this.user) &&
      !this.isAccounting(this.user)
    )
  }

  canEnableSignature() {
    return (
      !this.isReadOnly(this.user) &&
      !this.isAdmin(this.user) &&
      (this.isPropertyManager(this.user.roles) || this.isPropertyOwner(this.user))
    )
  }

  canEnableOwnerCenter() {
    return !this.isReadOnly(this.user) && !this.isAdmin(this.user) && !this.isStaff(this.user)
  }

  canEnableRategenie() {
    return (
      !this.isReadOnly(this.user) && !this.isAdmin(this.user) && !this.isStaff(this.user) && !this.isOwner(this.user)
    )
  }

  canEnableWebready() {
    return (
      !this.isReadOnly(this.user) && !this.isAdmin(this.user) && !this.isStaff(this.user) && !this.isOwner(this.user)
    )
  }

  canEnableChecklist() {
    const roles = [this.infoForm.get('role').value].map((role) => Number(role))
    return this.isPropertyManager(roles)
  }

  canRestrictRentals() {
    return !this.isAdmin(this.user) && !this.isOwner(this.user)
  }

  onSearchRental(term: string | Event) {
    if (R.is(Event, term)) {
      return
    }
    this.rentalTablePaginator.firstPage()
    this.rentalDataSource.filter = (<string>term).trim().toLowerCase()
    this.ownerRentalsSearchTerm = (<string>term).trim().toLowerCase()
  }

  @SaveForm()
  onSave(form: FormGroup) {
    if (this.locationForm.invalid || this.infoForm.invalid) {
      return
    }
    const infoData = this.infoForm.getRawValue()
    const formData = {
      ...this.locationForm.getRawValue(),
      ...infoData,
      attributes: {
        ...this.user.attributes,
        ...infoData.attributes,
      },
    } as UserSettingsForm

    if (
      this.admins.length <= 1 &&
      userRoleToString(R.map((r) => parseFloat(r), [formData.role])) !== USER_ROLES.admin.name &&
      this.admins[0].id === this.user.id
    ) {
      this.toast.error('This is the last administrator, You cannot remove it')
      return
    }

    if (this.emails.indexOf(formData.email) > -1) {
      this.toast.error('User with this email already exist.')
      return
    }

    if (!isSomething(formData.role)) {
      this.toast.error('You must have to specify at least one role.')
      return
    }

    this.store.dispatch(UpdateUser({ userId: this.user.id, form: formData }))
    this.tryToUnrestrictOwner(formData)
    this.close()
  }

  tryToUnrestrictOwner(form: UserSettingsForm) {
    // tslint:disable-next-line: triple-equals
    if (
      userRoleToNumber(this.user.roles) !== USER_ROLES.owner.value &&
      parseFloat(form.role) == USER_ROLES.owner.value
    ) {
      const rentals = R.filter((rental) => {
        return R.contains(this.user.id, R.defaultTo([], rental.restrictedUsers))
      }, this.rentals)
      if (rentals.length > 0) {
        this.store.dispatch(unRestrictUserAll({ rentals, userId: this.user.id }))
      }
    }
  }

  onToggleRestrictedAll(event: MatCheckboxChange) {
    this.confirmDialogService
      .confirm({
        title: event.checked ? 'Restrict all rentals?' : 'Unrestrict all rentals?',
        body: 'This option changes every rental in your current filter and not just the visible rentals. Would you like to continue?',
      })
      .subscribe(() => {
        if (event.checked) {
          this.store.dispatch(restrictUserAll({ rentals: this.rentals, userId: this.user.id }))
        } else {
          this.store.dispatch(unRestrictUserAll({ rentals: this.rentals, userId: this.user.id }))
        }
      })
  }

  onToggleRestricted(event: MatCheckboxChange, rental: Rental) {
    if (event.checked) {
      this.store.dispatch(restrictUser({ rental, userId: this.user.id }))
    } else {
      this.store.dispatch(unRestrictUser({ rental, userId: this.user.id }))
    }
  }

  private getCountryCode() {
    let countryCode = R.pathOr('', ['address', 'countryCode'], this.user)
    const countryName = R.pathOr('', ['address', 'country'], this.user)
    if (R.isEmpty(countryCode) && !R.isEmpty(countryName)) {
      const country = R.find((c: { id: string; name: string }) => c.name === countryName)(this.countryList)
      return R.pathOr('', ['id'], country)
    }
    return countryCode
  }
}
